"use client";

import { X } from "lucide-react";
import { useState, KeyboardEvent } from "react";
import { Control, useController, useFormContext } from "react-hook-form";
import { cn, isValidEmail } from "@drpcrd/utils";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./form";

type Props = {
  label: string;
  description?: string;
  name: string;
  control: Control<any>;
  placeholder?: string;
};

/**
 * Multi-email input component that allows adding and removing multiple email addresses
 */
export const MultiEmailInput = ({
  control,
  name,
  label,
  description,
  placeholder = "Add email",
}: Props) => {
  const [inputValue, setInputValue] = useState("");
  const { field, fieldState } = useController({ control, name });
  const { setError, clearErrors } = useFormContext();
  const emails: string[] = field.value || [];

  const addEmail = () => {
    const trimmedEmail = inputValue.trim();

    if (!trimmedEmail) {
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError(name, { message: "Please enter a valid email address" });
      return;
    }

    if (emails.includes(trimmedEmail)) {
      setError(name, { message: "This email has already been added" });
      return;
    }

    field.onChange([...emails, trimmedEmail]);
    setInputValue("");
    clearErrors(name);
  };

  const removeEmail = (emailToRemove: string) => {
    field.onChange(emails.filter((email) => email !== emailToRemove));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addEmail();
    }
  };

  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem className="space-y-1.5">
          <div>
            <FormLabel className="flex w-full flex-row gap-1 font-sans text-sm font-semibold">
              {label}
            </FormLabel>
            {description && (
              <FormDescription className="mt-0! mb-1!">
                {description}
              </FormDescription>
            )}
          </div>

          <FormControl className="w-full">
            <div
              className={cn(
                "flex w-full flex-wrap items-center gap-1 rounded-lg border border-neutral-500 bg-white leading-3",
                emails.length > 0 ? "px-1 py-[7px]" : "px-2 py-[9px]",
                fieldState.error
                  ? "border-pink-500 focus-within:border-pink-500"
                  : "border-neutral-500 focus-within:outline-2 focus-within:outline-black",
              )}
            >
              {emails.map((email) => (
                <div
                  key={email}
                  className="bg-primary-50 flex items-center gap-1 rounded py-0.5 pr-1 pl-1.5 text-lg leading-3"
                >
                  <span>{email}</span>
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    className="cursor-pointer"
                  >
                    <X className="h-2 w-2 font-semibold text-black" />
                  </button>
                </div>
              ))}
              <input
                type="email"
                name={name}
                id={name}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  clearErrors(name);
                }}
                onBlur={addEmail}
                onKeyDown={onKeyDown}
                placeholder={emails.length === 0 ? placeholder : ""}
                autoComplete="off"
                className="autofill-white placeholder:text-text-secondary min-w-[120px] flex-1 border-none bg-transparent font-sans text-lg font-normal outline-none"
              />
            </div>
          </FormControl>

          <FormMessage className="font-normal text-pink-500" />
        </FormItem>
      )}
    />
  );
};
